import * as React from 'react';

// A smaller logical canvas makes fixed-size HUD controls and panels larger on phones.
export const GAME_WIDTH = 1168;
export const GAME_HEIGHT = 657;

export function fitGameViewport(width: number, height: number) {
  const scale = Math.min(width / GAME_WIDTH, height / GAME_HEIGHT);
  return { width: Math.ceil(width / scale), scale };
}

export function isMobileGameDevice() {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
}

// Use the browser's layout viewport rather than a CSS transform: fixed panels,
// body portals, Phaser input and DOM hit testing then share desktop coordinates.
export function useMobileGameViewport() {
  const [mobile] = React.useState(isMobileGameDevice);
  React.useLayoutEffect(() => {
    if (!mobile) return;
    let meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'viewport';
      document.head.appendChild(meta);
    }
    const original = meta.content;
    const nativeApp = /PKNiN\//i.test(navigator.userAgent);
    const viewportContent = (fit: ReturnType<typeof fitGameViewport>) => {
      const scale = fit.scale.toFixed(6);
      return `width=${fit.width}, initial-scale=${scale}, viewport-fit=contain` +
        (nativeApp ? `, minimum-scale=${scale}, maximum-scale=${scale}, user-scalable=no` : '');
    };
    const root = document.documentElement;
    const originalHeight = root.style.getPropertyValue('--game-visible-height');
    let timer: ReturnType<typeof setTimeout>;
    let restoreTimer: ReturnType<typeof setTimeout>;
    let releaseTimer: ReturnType<typeof setTimeout>;
    let restoring = false;
    let suspended = false;
    let lastSize: { width: number; height: number } | undefined;
    const editing = () => document.activeElement?.matches('input, textarea, [contenteditable="true"]');
    const update = () => {
      // Keep the game stable when the on-screen keyboard opens for chat.
      if (suspended || editing() || restoring) return;
      const viewport = window.visualViewport;
      // 100vh includes the area behind mobile browser toolbars on some devices.
      // The visual viewport reports only the area that the player can see.
      const physicalWidth = viewport ? viewport.width * viewport.scale : window.innerWidth;
      const physicalHeight = viewport ? viewport.height * viewport.scale : window.innerHeight;
      if (!physicalWidth || !physicalHeight) return;
      lastSize = { width: Math.round(physicalWidth), height: Math.round(physicalHeight) };
      const fit = fitGameViewport(Math.round(physicalWidth), Math.round(physicalHeight));
      // Use game-layout coordinates immediately, even before the browser has
      // applied the new viewport scale. Otherwise the bottom HUD can be clipped.
      root.style.setProperty('--game-visible-height', `${physicalHeight / fit.scale}px`);
      const content = viewportContent(fit);
      if (meta!.content !== content) meta!.content = content;
      // Bounds consumers also need to refresh after the browser applies the meta.
      clearTimeout(timer);
      timer = setTimeout(() => window.dispatchEvent(new Event('gameviewportchange')), 150);
    };
    const focusIn = () => {
      if (suspended) return;
      clearTimeout(restoreTimer);
      clearTimeout(releaseTimer);
      restoring = false;
      if (!nativeApp) meta!.content = meta!.content.replace(/, maximum-scale=[\d.]+/, '');
    };
    const focusOut = () => {
      if (suspended) return;
      clearTimeout(restoreTimer);
      restoring = true;
      // Wait for focus transfer; moving between inputs should keep the keyboard open.
      restoreTimer = setTimeout(() => {
        if (suspended || editing() || !lastSize) return;
        restoring = true;
        const fit = fitGameViewport(lastSize.width, lastSize.height);
        const content = viewportContent(fit);
        // Reassigning initial-scale alone does not undo input auto-zoom on all browsers.
        // Temporarily cap the scale, then restore normal pinch zoom after the keyboard animation.
        meta!.content = nativeApp ? content : `${content}, maximum-scale=${fit.scale.toFixed(6)}`;
        root.style.setProperty('--game-visible-height', `${lastSize.height / fit.scale}px`);
        window.scrollTo(0, 0);
        window.dispatchEvent(new Event('gameviewportchange'));
        releaseTimer = setTimeout(() => {
          meta!.content = content;
          restoring = false;
          update();
        }, 700);
      }, 350);
    };
    const accountViewport = () => {
      suspended = root.classList.contains('uc-open');
      clearTimeout(timer); clearTimeout(restoreTimer); clearTimeout(releaseTimer);
      restoring = false;
      if (suspended) {
        root.classList.remove('mobile-game-viewport');
        meta!.content = original;
        timer = setTimeout(() => window.dispatchEvent(new Event('resize')),150);
      } else {
        root.classList.add('mobile-game-viewport');
        update();
        timer = setTimeout(update,150);
      }
    };
    document.documentElement.classList.add('mobile-game-viewport');
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    window.visualViewport?.addEventListener('resize', update);
    document.addEventListener('fullscreenchange', update);
    document.addEventListener('focusin', focusIn);
    document.addEventListener('focusout', focusOut);
    window.addEventListener('pknin:account-viewport', accountViewport);
    return () => {
      clearTimeout(timer);
      clearTimeout(restoreTimer);
      clearTimeout(releaseTimer);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      window.visualViewport?.removeEventListener('resize', update);
      document.removeEventListener('fullscreenchange', update);
      document.removeEventListener('focusin', focusIn);
      document.removeEventListener('focusout', focusOut);
      window.removeEventListener('pknin:account-viewport', accountViewport);
      document.documentElement.classList.remove('mobile-game-viewport');
      if (originalHeight) root.style.setProperty('--game-visible-height', originalHeight);
      else root.style.removeProperty('--game-visible-height');
      if (created) meta!.remove(); else meta!.content = original;
    };
  }, [mobile]);
  return mobile;
}
